/**
 * CartDrawer Component
 *
 * Slide-out cart panel with:
 * - Smooth animations
 * - Item list with AnimatePresence
 * - Cart summary
 * - Checkout button
 * - Empty state
 * - Full funnel analytics instrumentation
 *
 * @file CartDrawer.tsx
 * @version 2.3.0 - Refactored with Tangi Design System + Analytics + React.memo
 */
import React, { useCallback, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import * as Dialog from '@radix-ui/react-dialog';
import { ArrowRight, ShoppingBag, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  Box,
  Button,
  Center,
  Drawer,
  EmptyState,
  ErrorView,
  Icon,
  Row,
  Skeleton,
  SkeletonGroup,
  Stack,
  Text
} from '@/ui';
import { DROP_OFF_EVENTS, FUNNEL_EVENTS } from '@/shared/analytics';
import { useAnalytics } from '@/shared/hooks';

import { useCartStore } from '../hooks/useCartStore';
import { useCart, useClearCart } from '../hooks/useCart';
import { CartItem } from './CartItem';
import { CartSummary } from './CartSummary';

// Module-level animation constants to prevent re-renders
const backdropAnimation = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.2 },
} as const;

const drawerAnimation = {
  initial: { x: '100%' },
  animate: { x: 0 },
  exit: { x: '100%' },
  transition: { type: 'spring', damping: 30, stiffness: 300 },
} as const;

const itemCountAnimation = {
  initial: { scale: 1.2 },
  animate: { scale: 1 },
} as const;

const closeButtonHover = { scale: 1.1, rotate: 90 } as const;
const closeButtonTap = { scale: 0.9 } as const;

const footerAnimation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
} as const;

export const CartDrawer = React.memo(function CartDrawer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trackFunnel, trackDropOff } = useAnalytics();
  const cartOpenTime = useRef<number | null>(null);
  const hasTrackedCartView = useRef(false);

  const { isDrawerOpen, closeDrawer } = useCartStore();
  const { data: cart, isLoading, isError, refetch } = useCart();
  const { mutate: clearCart, isPending: isClearing } = useClearCart();

  const itemCount = cart?.items.length || 0;
  const hasItems = itemCount > 0;

  // Track cart viewed when drawer opens with items
  useEffect(() => {
    if (isDrawerOpen && cart && hasItems && !hasTrackedCartView.current) {
      hasTrackedCartView.current = true;
      cartOpenTime.current = Date.now();

      trackFunnel(FUNNEL_EVENTS.CART_VIEWED, {
        item_count: itemCount,
        total_value: cart.total_amount_major,
        currency: cart.currency,
      });
    }

    // Reset tracking when drawer closes
    if (!isDrawerOpen) {
      hasTrackedCartView.current = false;
    }
  }, [isDrawerOpen, cart, hasItems, itemCount, trackFunnel]);

  // Track cart abandonment when drawer closes with items
  const handleDrawerClose = useCallback(() => {
    if (hasItems && cartOpenTime.current) {
      const timeInCart = Date.now() - cartOpenTime.current;
      trackDropOff(DROP_OFF_EVENTS.CART_ABANDONED, {
        cart_total: cart?.total_amount_major ?? 0,
        item_count: itemCount,
        time_in_cart_ms: timeInCart,
        currency: cart?.currency ?? 'USD',
      });
    }
    closeDrawer();
  }, [hasItems, cart, itemCount, trackDropOff, closeDrawer]);

  const handleCheckout = useCallback((): void => {
    // Track checkout started
    if (cart) {
      trackFunnel(FUNNEL_EVENTS.CHECKOUT_STARTED, {
        cart_total: cart.total_amount_major,
        item_count: itemCount,
        currency: cart.currency,
      });
    }

    closeDrawer();
    // Navigate to checkout with order info
    if (cart?.order_id) {
      void navigate(`/checkout?order=${cart.order_id}`);
    } else {
      void navigate('/checkout');
    }
  }, [cart, itemCount, trackFunnel, closeDrawer, navigate]);

  const handleContinueShopping = (): void => {
    closeDrawer();
    void navigate('/workshop');
  };

  const handleClearCart = (): void => {
    clearCart(undefined, {
      onSuccess: () => {
        toast.success(t('ui.cart.cartCleared'));
      },
      onError: (error) => {
        toast.error(t('ui.cart.failedToClearCart'), {
          description: error.message,
        });
      },
    });
  };

  return (
    <Dialog.Root open={isDrawerOpen} onOpenChange={(open) => !open && handleDrawerClose()}>
      <AnimatePresence>
        {isDrawerOpen && (
          <Dialog.Portal forceMount>
            {/* Backdrop */}
            <Dialog.Overlay asChild>
              <motion.div
                initial={backdropAnimation.initial}
                animate={backdropAnimation.animate}
                exit={backdropAnimation.exit}
                transition={backdropAnimation.transition}
              >
                <Drawer.Overlay />
              </motion.div>
            </Dialog.Overlay>

            {/* Drawer Content
                                Responsive Behavior:
                                - Mobile (< md/768px): 90% width with 10% visible backdrop
                                  → Maintains drawer UX metaphor (not full-screen modal)
                                  → Reduces cart abandonment (lower commitment anxiety)
                                  → Utilizes backdrop blur effect
                                  → Provides visual swipe gesture affordance
                                - Desktop (≥ md/768px): 420px fixed width
                                  → Classic drawer pattern with page content visible
                                  → md breakpoint (768px) > drawer width (420px) prevents overflow
                                Safe Areas: Both left/right insets for landscape notched devices
                            */}
            <Dialog.Content asChild>
              <motion.div
                data-testid="cart-drawer"
                initial={drawerAnimation.initial}
                animate={drawerAnimation.animate}
                exit={drawerAnimation.exit}
                transition={drawerAnimation.transition}
              >
                <Drawer.Content>
                {/* Header - Minimal, Clean */}
                <Drawer.Header>
                  <Row align="center" justify="between">
                  <Dialog.Title>
                    <Row align="center" gap={2}>
                      <Text size="heading-3" weight="semibold" variant="default">
                        {t('ui.cart.yourCart')}
                      </Text>
                      {hasItems && (
                        <motion.span
                          key={itemCount}
                          initial={itemCountAnimation.initial}
                          animate={itemCountAnimation.animate}
                        >
                          <Text size="heading-3" variant="muted" weight="normal">
                            ({itemCount})
                          </Text>
                        </motion.span>
                      )}
                    </Row>
                  </Dialog.Title>
                  <Dialog.Close asChild>
                    <Button
                      asChild
                      variant="ghost"
                      size="icon"
                    >
                      <motion.button
                        whileHover={closeButtonHover}
                        whileTap={closeButtonTap}
                        aria-label="Close"
                      >
                        <Icon icon={X} size="md" color="muted" />
                      </motion.button>
                    </Button>
                  </Dialog.Close>
                  </Row>
                </Drawer.Header>

                {/* Content */}
                <Drawer.ScrollArea>
                  {isLoading ? (
                    // Loading State - Using Skeleton components
                    <Box padding="lg">
                      <SkeletonGroup>
                        {[1, 2, 3].map((i) => (
                          <Row align="start" gap={4} key={i}>
                            <Skeleton variant="rounded" width={80} height={80} />
                            <Stack gap={2} style={{ flex: 1 }}>
                              <Skeleton variant="text" width="75%" />
                              <Skeleton variant="text" width="50%" />
                              <Skeleton variant="text" width="25%" />
                            </Stack>
                          </Row>
                        ))}
                      </SkeletonGroup>
                    </Box>
                  ) : isError ? (
                    // Error State - Using ErrorView component
                    <Box padding="lg">
                      <Center fullHeight>
                        <ErrorView
                          title={t('ui.cart.failedToLoadCart')}
                          message={t('ui.cart.pleaseTryAgain')}
                          onRetry={() => void refetch()}
                        />
                      </Center>
                    </Box>
                  ) : !hasItems ? (
                    // Empty State - Using EmptyState component
                    <Box padding="lg">
                      <Center fullHeight>
                        <EmptyState
                          title={t('ui.cart.empty')}
                          description={t('ui.cart.createSomethingMagical')}
                          icon={ShoppingBag}
                          actionLabel={t('ui.cart.startCreating')}
                          onAction={handleContinueShopping}
                        />
                      </Center>
                    </Box>
                  ) : (
                    // Cart Items - Clean, Spacious Layout
                    <Box padding="lg">
                      <Stack gap={6}>
                        {/* Clear Cart Button - Subtle, Top Right */}
                        <Row justify="end">
                          <Button
                            variant="ghost-destructive"
                            size="sm"
                            onClick={handleClearCart}
                            disabled={isClearing}
                          >
                            <Text size="xs" variant="muted">
                              {isClearing ? t('ui.cart.clearing') : t('ui.cart.clearAll')}
                            </Text>
                          </Button>
                        </Row>

                        {/* Items List - Generous Spacing */}
                        <Stack gap={6}>
                          <AnimatePresence mode="popLayout">
                            {cart?.items.map((item) => (
                              <CartItem key={item.id} item={item} />
                            ))}
                          </AnimatePresence>
                        </Stack>
                      </Stack>
                    </Box>
                  )}
                </Drawer.ScrollArea>

                {/* Footer - Only show when there are items */}
                {hasItems && (
                  <Drawer.Footer>
                    <motion.div
                      initial={footerAnimation.initial}
                      animate={footerAnimation.animate}
                    >
                    {/* Summary - Clean, Spacious */}
                    <Box padding="lg">
                      <Stack gap={6}>
                        <CartSummary />

                        {/* Primary Action - THE HERO */}
                        <Stack gap={3}>
                          <Button
                            onClick={handleCheckout}
                            variant="primary"
                            size="lg"
                            fullWidth
                          >
                            {t('ui.cart.proceedToCheckout')}
                            <Icon icon={ArrowRight} size="sm" />
                          </Button>

                          <Button
                            onClick={handleContinueShopping}
                            variant="ghost"
                            fullWidth
                          >
                            {t('ui.cart.continueShopping')}
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                    </motion.div>
                  </Drawer.Footer>
                )}
                </Drawer.Content>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
});
